// useSurfaceNodes.js — live Firestore overlay for Ai surface snapshots

import { useEffect, useMemo, useState } from 'react';

import { subscribeToNodesByKeys } from 'services/nodes/NodeReadService';

function collectNodeKeys(nodes = []) {
  return Array.from(new Set(
    (Array.isArray(nodes) ? nodes : [])
      .map((node) => String(node?.key || node?.id || '').trim())
      .filter(Boolean),
  ));
}

/**
 * Subscribes to the node keys from a surface snapshot while keeping the snapshot
 * as the initial render payload until the first subscription emit arrives.
 */
export default function useSurfaceNodes(snapshotNodes = [], { enabled = true } = {}) {
  const nodeKeys = useMemo(() => collectNodeKeys(snapshotNodes), [snapshotNodes]);
  const [subscribedNodes, setSubscribedNodes] = useState(null);

  useEffect(() => {
    if (!enabled || nodeKeys.length === 0) {
      setSubscribedNodes(null);
      return undefined;
    }

    const unsubscribe = subscribeToNodesByKeys(nodeKeys, (rows) => {
      setSubscribedNodes(Array.isArray(rows) ? rows : []);
    });

    return () => {
      unsubscribe?.();
      setSubscribedNodes(null);
    };
  }, [enabled, nodeKeys]);

  const nodes = subscribedNodes ?? snapshotNodes;

  return {
    nodes,
    isLive: subscribedNodes !== null,
  };
}
